import { createSlice } from '@reduxjs/toolkit';
import { UserRole } from './models/user-role.model';
import { UserGroup } from './models/user-group.model';
import { Permission } from './models/permission.model';
import { SortModel } from './models/sort-model.model';
import securityService from '../services/securityService';
import * as uiSlice from './ui.slice';

interface SecurityState {
  loadingUserRoles: boolean;
  loadedUserRoles: boolean;
  userRoles: UserRole[];
  loadingAllRoles: boolean;
  loadedAllRoles: boolean;
  allRoles: UserRole[];
  loadingUserGroups: boolean;
  loadedUserGroups: boolean;
  userGroups: UserGroup[];
  loadingUserPermissions: boolean;
  loadedUserPermissions: boolean;
  userPermissions: Permission[];
  savingUserPermission: boolean;
  savedUserPermission: boolean;
  allPermissions: Permission[];
  loadingAllPermissions: boolean;
  loadedAllPermissions: boolean;
  savingUserGroup: boolean;
  savedUserGroup: boolean;
  allGroups: UserGroup[];
  loadingAllGroups: boolean;
  loadedAllGroups: boolean;
  savingUserRole: boolean;
  savedUserRole: boolean;
}

export const initialState: SecurityState = {
  loadingUserRoles: false,
  loadedUserRoles: false,
  userRoles: [],
  loadingAllRoles: false,
  loadedAllRoles: false,
  allRoles: [],
  loadingUserGroups: false,
  loadedUserGroups: false,
  userGroups: [],
  loadingUserPermissions: false,
  loadedUserPermissions: false,
  userPermissions: [],
  savingUserPermission: false,
  savedUserPermission: false,
  allPermissions: [],
  loadingAllPermissions: false,
  loadedAllPermissions: false,
  savingUserGroup: false,
  savedUserGroup: false,
  allGroups: [],
  loadingAllGroups: false,
  loadedAllGroups: false,
  savingUserRole: false,
  savedUserRole: false,
};

export const slice = createSlice({
  name: 'ui',
  initialState,
  reducers: {
    setLoadingUserRoles: (
      state: SecurityState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadingUserRoles = action.payload;
    },
    setLoadedUserRoles: (
      state: SecurityState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadedUserRoles = action.payload;
    },
    setUserRoles: (state: SecurityState, action: { payload: UserRole[] }) => {
      state.loadingUserRoles = false;
      state.loadedUserRoles = true;
      state.userRoles = action.payload;
    },
    setLoadingAllRoles: (
      state: SecurityState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadingAllRoles = action.payload;
    },
    setLoadedAllRoles: (
      state: SecurityState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadedAllRoles = action.payload;
    },
    setAllRoles: (state: SecurityState, action: { payload: UserRole[] }) => {
      state.loadingAllRoles = false;
      state.loadedAllRoles = true;
      state.allRoles = action.payload;
    },
    setLoadingUserGroups: (
      state: SecurityState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadingUserGroups = action.payload;
    },
    setLoadedUserGroups: (
      state: SecurityState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadedUserGroups = action.payload;
    },
    setUserGroups: (state: SecurityState, action: { payload: UserGroup[] }) => {
      state.loadingUserGroups = false;
      state.loadedUserGroups = true;
      state.userGroups = action.payload;
    },
    setLoadingUserPermissions: (
      state: SecurityState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadingUserPermissions = action.payload;
    },
    setLoadedUserPermissions: (
      state: SecurityState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadedUserPermissions = action.payload;
    },
    setUserPermissions: (
      state: SecurityState,
      action: { payload: Permission[] },
    ) => {
      state.loadingUserPermissions = false;
      state.loadedUserPermissions = true;
      state.userPermissions = action.payload;
    },
    setSavingUserPermission: (
      state: SecurityState,
      action: { type: string; payload: boolean },
    ) => {
      state.savingUserPermission = action.payload;
    },
    setSavedUserPermission: (
      state: SecurityState,
      action: { type: string; payload: boolean },
    ) => {
      state.savedUserPermission = action.payload;
    },
    setAllPermissions: (
      state: SecurityState,
      action: { payload: Permission[] },
    ) => {
      state.allPermissions = action.payload;
    },
    setLoadingAllPermissions: (
      state,
      { payload }: { type: string; payload: boolean },
    ) => {
      state.loadingAllPermissions = payload;
    },
    setLoadedAllPermissions: (
      state,
      { payload }: { type: string; payload: boolean },
    ) => {
      state.loadedAllPermissions = payload;
    },
    addUserPermission: (
      state: SecurityState,
      action: { type: string; payload: Permission },
    ) => {
      const permissions = [action.payload, ...state.userPermissions];
      state.userPermissions = permissions;
    },
    updateUserPermission: (
      state: SecurityState,
      action: { type: string; payload: Permission },
    ) => {
      const permissions = [...state.userPermissions];
      const idx = permissions.findIndex(
        (g) => g.userPermissionID === action.payload.userPermissionID,
      );
      if (idx >= 0) {
        permissions.splice(idx, 1, action.payload);
        state.userPermissions = permissions;
      }
    },
    deleteUserPermission: (
      state: SecurityState,
      action: { type: string; payload: number },
    ) => {
      const permissions = [...state.userPermissions];
      const idx = permissions.findIndex(
        (g) => g.userPermissionID === action.payload,
      );
      if (idx >= 0) {
        permissions.splice(idx, 1);
        state.userPermissions = permissions;
      }
    },
    setSavingUserGroup: (
      state: SecurityState,
      action: { type: string; payload: boolean },
    ) => {
      state.savingUserGroup = action.payload;
    },
    setSavedUserGroup: (
      state: SecurityState,
      action: { type: string; payload: boolean },
    ) => {
      state.savedUserGroup = action.payload;
    },
    addUserGroup: (
      state: SecurityState,
      action: { type: string; payload: UserGroup },
    ) => {
      const groups = [action.payload, ...state.userGroups];
      state.userGroups = groups;
    },
    updateUserGroup: (
      state: SecurityState,
      action: { type: string; payload: UserGroup },
    ) => {
      const groups = [...state.userGroups];
      const idx = groups.findIndex(
        (g) => g.userGroupID === action.payload.userGroupID,
      );
      if (idx >= 0) {
        groups.splice(idx, 1, action.payload);
        state.userGroups = groups;
      }
    },
    deleteUserGroup: (
      state: SecurityState,
      action: { type: string; payload: number },
    ) => {
      const groups = [...state.userGroups];
      const idx = groups.findIndex((g) => g.userGroupID === action.payload);
      if (idx >= 0) {
        groups.splice(idx, 1);
        state.userGroups = groups;
      }
    },
    setLoadingAllGroups: (
      state: SecurityState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadingAllGroups = action.payload;
    },
    setLoadedAllGroups: (
      state: SecurityState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadedAllGroups = action.payload;
    },
    setAllGroups: (state: SecurityState, action: { payload: UserGroup[] }) => {
      state.loadingAllGroups = false;
      state.loadedAllGroups = true;
      state.allGroups = action.payload;
    },

    setSavingUserRole: (
      state: SecurityState,
      action: { type: string; payload: boolean },
    ) => {
      state.savingUserRole = action.payload;
    },
    setSavedUserRole: (
      state: SecurityState,
      action: { type: string; payload: boolean },
    ) => {
      state.savedUserRole = action.payload;
    },
    addUserRole: (
      state: SecurityState,
      action: { type: string; payload: UserRole },
    ) => {
      const roles = [action.payload, ...state.userRoles];
      state.userRoles = roles;
    },
    updateUserRole: (
      state: SecurityState,
      action: { type: string; payload: UserRole },
    ) => {
      const roles = [...state.userRoles];
      const idx = roles.findIndex(
        (g) => g.userRoleID === action.payload.userRoleID,
      );
      if (idx >= 0) {
        roles.splice(idx, 1, action.payload);
        state.userRoles = roles;
      }
    },
    deleteUserRole: (
      state: SecurityState,
      action: { type: string; payload: number },
    ) => {
      const roles = [...state.userRoles];
      const idx = roles.findIndex((g) => g.userRoleID === action.payload);
      if (idx >= 0) {
        roles.splice(idx, 1);
        state.userRoles = roles;
      }
    },
  },
});

export const { setLoadingUserRoles } = slice.actions;
export const { setLoadedUserRoles } = slice.actions;
export const { setUserRoles } = slice.actions;
export const { setLoadingAllRoles } = slice.actions;
export const { setLoadedAllRoles } = slice.actions;
export const { setAllRoles } = slice.actions;
export const { setLoadingUserGroups } = slice.actions;
export const { setLoadedUserGroups } = slice.actions;
export const { setUserGroups } = slice.actions;
export const { setLoadingUserPermissions } = slice.actions;
export const { setLoadedUserPermissions } = slice.actions;
export const { setUserPermissions } = slice.actions;
export const { setSavingUserPermission } = slice.actions;
export const { setSavedUserPermission } = slice.actions;
export const { setLoadingAllPermissions } = slice.actions;
export const { setLoadedAllPermissions } = slice.actions;
export const { setAllPermissions } = slice.actions;
export const { addUserPermission } = slice.actions;
export const { updateUserPermission } = slice.actions;
export const { deleteUserPermission } = slice.actions;
export const { setSavingUserGroup } = slice.actions;
export const { setSavedUserGroup } = slice.actions;
export const { addUserGroup } = slice.actions;
export const { updateUserGroup } = slice.actions;
export const { deleteUserGroup } = slice.actions;
export const { setLoadingAllGroups } = slice.actions;
export const { setLoadedAllGroups } = slice.actions;
export const { setAllGroups } = slice.actions;
export const { setSavingUserRole } = slice.actions;
export const { setSavedUserRole } = slice.actions;
export const { addUserRole } = slice.actions;
export const { updateUserRole } = slice.actions;
export const { deleteUserRole } = slice.actions;

export const fetchUserRolesAsync =
  ({
    categoryId,
    appTitleId,
    searchText,
    sortModel,
  }: {
    categoryId?: number;
    appTitleId?: number;
    searchText?: string;
    sortModel?: { column: string; direction: 'asc' | 'desc' } | null;
  } = {}) =>
  (dispatch: Function) => {
    dispatch(setUserRoles([]));
    dispatch(setLoadingUserRoles(true));
    dispatch(setLoadedUserRoles(false));
    securityService
      .getUserRoles(categoryId, appTitleId, searchText, sortModel)
      .subscribe((userRoles) => {
        dispatch(setUserRoles(userRoles));
        dispatch(setLoadingUserRoles(false));
        dispatch(setLoadedUserRoles(true));
      });
  };

export const fetchUserGroupsAsync =
  (searchText: string = '', sortModel: SortModel | null = null) =>
  (dispatch: Function) => {
    dispatch(setUserGroups([]));
    dispatch(setLoadingUserGroups(true));
    dispatch(setLoadedUserGroups(false));
    securityService
      .getUserGroups(searchText, sortModel)
      .subscribe((userGroups) => {
        dispatch(setUserGroups(userGroups));
        dispatch(setLoadingUserGroups(false));
        dispatch(setLoadedUserGroups(true));
      });
  };
export const fetchAllGroupsForExportAsync =
  (searchText: string = '', sortModel: SortModel | null = null) =>
  (dispatch: Function) => {
    dispatch(setLoadingAllGroups(true));
    dispatch(setLoadedAllGroups(false));
    securityService.getUserGroups(searchText, sortModel).subscribe({
      next: (allGroups) => {
        dispatch(setAllGroups(allGroups));
        dispatch(setLoadingAllGroups(false));
        dispatch(setLoadedAllGroups(true));
      },
      error: (err) => {
        dispatch(setLoadingAllGroups(false));
        dispatch(setLoadedAllGroups(false));
      },
    });
  };

export const fetchAllRolesForExportAsync =
  (searchText: string = '', sortModel: SortModel | null = null) =>
  (dispatch: Function) => {
    dispatch(setLoadingAllRoles(true));
    dispatch(setLoadedAllRoles(false));
    securityService
      .getUserRoles(undefined, undefined, searchText, sortModel)
      .subscribe({
        next: (allRoles) => {
          dispatch(setAllRoles(allRoles));
          dispatch(setLoadingAllRoles(false));
          dispatch(setLoadedAllRoles(true));
        },
        error: (err) => {
          dispatch(setLoadingAllRoles(false));
          dispatch(setLoadedAllRoles(false));
        },
      });
  };

export const fetchUserPermissionsAsync =
  (search: string = '', sortModel: SortModel | null = null) =>
  (dispatch: Function) => {
    dispatch(setUserPermissions([]));
    dispatch(setLoadingUserPermissions(true));
    dispatch(setLoadedUserPermissions(false));
    securityService
      .getUserPermissions(search, sortModel)
      .subscribe((userPermissions) => {
        dispatch(setUserPermissions(userPermissions));
        dispatch(setLoadingUserPermissions(false));
        dispatch(setLoadedUserPermissions(true));
      });
  };

export const fetchAllPermissionsForExportAsync =
  (search: string = '', sortModel: SortModel | null = null) =>
  (dispatch: Function) => {
    dispatch(setLoadingAllPermissions(true));
    dispatch(setLoadedAllPermissions(false));

    return new Promise<Permission[]>((resolve, reject) => {
      securityService.getUserPermissions(search, sortModel).subscribe({
        next: (all) => {
          dispatch(setAllPermissions(all));
          dispatch(setLoadingAllPermissions(false));
          dispatch(setLoadedAllPermissions(true));
          resolve(all);
        },
        error: (err) => {
          dispatch(setLoadingAllPermissions(false));
          dispatch(setLoadedAllPermissions(false));
          reject(err);
        },
      });
    });
  };

export const createUserPermissionAsync =
  (permission: Permission) => (dispatch: Function) => {
    dispatch(setSavingUserPermission(true));
    dispatch(setSavedUserPermission(false));
    securityService.createUserPermission(permission).subscribe({
      next: (newPermission) => {
        dispatch(addUserPermission(newPermission));
        dispatch(setSavingUserPermission(false));
        dispatch(setSavedUserPermission(true));
        dispatch(
          uiSlice.setSuccessMessage('snackbar.saveUserPermissionSuccess'),
        );
      },
      error: (err) => {
        dispatch(setSavingUserPermission(false));
        dispatch(setSavedUserPermission(false));
        dispatch(uiSlice.setErrorMessage(err.message));
      },
    });
  };

export const updateUserPermissionAsync =
  (permission: Permission) => (dispatch: Function) => {
    dispatch(setSavingUserPermission(true));
    dispatch(setSavedUserPermission(false));
    securityService.updateUserPermission(permission).subscribe({
      next: (updatedPermission) => {
        dispatch(updateUserPermission(updatedPermission));
        dispatch(setSavingUserPermission(false));
        dispatch(setSavedUserPermission(true));
        dispatch(
          uiSlice.setSuccessMessage('snackbar.saveUserPermissionSuccess'),
        );
      },
      error: (err) => {
        dispatch(setSavingUserPermission(false));
        dispatch(setSavedUserPermission(false));
        dispatch(uiSlice.setErrorMessage(err.message));
      },
    });
  };

export const deleteUserPermissionAsync =
  (permissionID: number) => (dispatch: Function) => {
    securityService.deleteUserPermission(permissionID).subscribe({
      next: () => {
        dispatch(deleteUserPermission(permissionID));
        dispatch(
          uiSlice.setSuccessMessage('snackbar.deleteUserPermissionSuccess'),
        );
      },
      error: (err) => {
        dispatch(uiSlice.setErrorMessage(err.message));
      },
    });
  };

export const createUserGroupAsync =
  (group: UserGroup) => (dispatch: Function) => {
    dispatch(setSavingUserGroup(true));
    dispatch(setSavedUserGroup(false));
    securityService.createUserGroup(group).subscribe({
      next: (newGroup) => {
        dispatch(addUserGroup(newGroup));
        dispatch(setSavingUserGroup(false));
        dispatch(setSavedUserGroup(true));
        dispatch(uiSlice.setSuccessMessage('snackbar.saveUserGroupSuccess'));
      },
      error: (err) => {
        dispatch(setSavingUserGroup(false));
        dispatch(setSavedUserGroup(false));
        dispatch(uiSlice.setErrorMessage(err.message));
      },
    });
  };

export const updateUserGroupAsync =
  (group: UserGroup) => (dispatch: Function) => {
    dispatch(setSavingUserGroup(true));
    dispatch(setSavedUserGroup(false));
    securityService.updateUserGroup(group).subscribe({
      next: (updatedGroup) => {
        dispatch(updateUserGroup(updatedGroup));
        dispatch(setSavingUserGroup(false));
        dispatch(setSavedUserGroup(true));
        dispatch(uiSlice.setSuccessMessage('snackbar.saveUserGroupSuccess'));
      },
      error: (err) => {
        dispatch(setSavingUserGroup(false));
        dispatch(setSavedUserGroup(false));
        dispatch(uiSlice.setErrorMessage(err.message));
      },
    });
  };

export const deleteUserGroupAsync =
  (groupID: number) => (dispatch: Function) => {
    securityService.deleteUserGroup(groupID).subscribe({
      next: () => {
        dispatch(deleteUserGroup(groupID));
        dispatch(uiSlice.setSuccessMessage('snackbar.deleteUserGroupSuccess'));
      },
      error: (err) => {
        dispatch(uiSlice.setErrorMessage(err.message));
      },
    });
  };

export const createUserRoleAsync = (role: UserRole) => (dispatch: Function) => {
  dispatch(setSavingUserRole(true));
  dispatch(setSavedUserRole(false));
  securityService.createUserRole(role).subscribe({
    next: (newRole) => {
      dispatch(addUserRole(newRole));
      dispatch(setSavingUserRole(false));
      dispatch(setSavedUserRole(true));
      dispatch(uiSlice.setSuccessMessage('snackbar.saveUserRoleSuccess'));
    },
    error: (err) => {
      dispatch(setSavingUserRole(false));
      dispatch(setSavedUserRole(false));
      dispatch(uiSlice.setErrorMessage(err.message));
    },
  });
};

export const updateUserRoleAsync = (role: UserRole) => (dispatch: Function) => {
  dispatch(setSavingUserRole(true));
  dispatch(setSavedUserRole(false));
  securityService.updateUserRole(role).subscribe({
    next: (updatedRole) => {
      dispatch(updateUserRole(updatedRole));
      dispatch(setSavingUserRole(false));
      dispatch(setSavedUserRole(true));
      dispatch(uiSlice.setSuccessMessage('snackbar.saveUserRoleSuccess'));
    },
    error: (err) => {
      dispatch(setSavingUserRole(false));
      dispatch(setSavedUserRole(false));
      dispatch(uiSlice.setErrorMessage(err.message));
    },
  });
};

export const deleteUserRoleAsync = (roleID: number) => (dispatch: Function) => {
  securityService.deleteUserRole(roleID).subscribe({
    next: () => {
      dispatch(deleteUserRole(roleID));
      dispatch(uiSlice.setSuccessMessage('snackbar.deleteUserRoleSuccess'));
    },
    error: (err) => {
      dispatch(uiSlice.setErrorMessage(err.message));
    },
  });
};

export const selectLoadingUserRoles = (state: { security: SecurityState }) =>
  state.security.loadingUserRoles;
export const selectLoadedUserRoles = (state: { security: SecurityState }) =>
  state.security.loadedUserRoles;
export const selectUserRoles = (state: { security: SecurityState }) =>
  state.security.userRoles;
export const selectAllRoles = (state: { security: SecurityState }) =>
  state.security.allRoles;
export const selectLoadingAllRoles = (state: { security: SecurityState }) =>
  state.security.loadingAllRoles;
export const selectLoadedAllRoles = (state: { security: SecurityState }) =>
  state.security.loadedAllRoles;
export const selectLoadingUserGroups = (state: { security: SecurityState }) =>
  state.security.loadingUserGroups;
export const selectLoadedUserGroups = (state: { security: SecurityState }) =>
  state.security.loadedUserGroups;
export const selectUserGroups = (state: { security: SecurityState }) =>
  state.security.userGroups;
export const selectLoadingUserPermissions = (state: {
  security: SecurityState;
}) => state.security.loadingUserPermissions;
export const selectLoadedUserPermissions = (state: {
  security: SecurityState;
}) => state.security.loadedUserPermissions;
export const selectUserPermissions = (state: { security: SecurityState }) =>
  state.security.userPermissions;
export const selectAllPermissions = (state: { security: SecurityState }) =>
  state.security.allPermissions;
export const selectLoadingAllPermissions = (state: {
  security: SecurityState;
}) => state.security.loadingAllPermissions;
export const selectLoadedAllPermissions = (state: {
  security: SecurityState;
}) => state.security.loadedAllPermissions;
export const selectSavingUserPermission = (state: {
  security: SecurityState;
}) => state.security.savingUserPermission;
export const selectSavedUserPermission = (state: { security: SecurityState }) =>
  state.security.savedUserPermission;
export const selectSavingUserGroup = (state: { security: SecurityState }) =>
  state.security.savingUserGroup;
export const selectSavedUserGroup = (state: { security: SecurityState }) =>
  state.security.savedUserGroup;
export const selectAllGroups = (state: { security: SecurityState }) =>
  state.security.allGroups;
export const selectLoadingAllGroups = (state: { security: SecurityState }) =>
  state.security.loadingAllGroups;
export const selectLoadedAllGroups = (state: { security: SecurityState }) =>
  state.security.loadedAllGroups;
export const selectSavingUserRole = (state: { security: SecurityState }) =>
  state.security.savingUserRole;
export const selectSavedUserRole = (state: { security: SecurityState }) =>
  state.security.savedUserRole;

export default slice.reducer;
